import { AttachmentActionEventSource, AttachmentEventLocation, ModalView } from "fond/attachments/types";
import { AttachmentEntityType, AttachmentFilterType } from "fond/types";
import { ViewMode } from "fond/widgets/FilesViewer/types";

import { boolean, event, number, oneOf, z } from "../schema-util";

import { featureId, projectId } from "./shared";

const attachmentBaseProps = {
  context: oneOf(AttachmentEntityType).describe("The context in which the event occurred"),
  location: oneOf(AttachmentEventLocation).optional().describe("The location where the event occurred"),
  filterType: oneOf(AttachmentFilterType)
    .optional()
    .describe("For 'Project' contexts will note whether you're looking at either project or feature attachments"), // only for items in the `Project` context
  featureId: featureId.optional(),
  projectId,
};
const attachmentEvent = event(attachmentBaseProps);
export type AttachmentBaseEventProps = z.infer<typeof attachmentEvent>;

const modalView = oneOf(ModalView).describe("The view mode of the modal, e.g., list or staging");

export const attachments = {
  "attachments.upload.clicked": attachmentEvent.describe("User clicked the upload button"),
  "attachments.upload.started": attachmentEvent.extend({ count: number().describe("Number of files about to be uploaded") }),
  "attachments.view.changed": attachmentEvent.extend({ view: oneOf(ViewMode).describe("The view mode the user has switched to") }),
  "attachments.upload.close.clicked": attachmentEvent,
  "attachments.staging.cancel.clicked": attachmentEvent,
  "attachments.upload.cancelled": attachmentEvent.extend({ count: number().describe("Number of files that were about to be uploaded") }),
  "attachments.upload.item.cancel.clicked": attachmentEvent,
  "attachments.upload.item.retry.clicked": attachmentEvent,
  "attachments.modal.opened": attachmentEvent.extend({ view: modalView }),
  "attachments.modal.closed": attachmentEvent.extend({ view: modalView }),
  "attachments.tab.clicked": attachmentEvent,
  "attachments.action.download.clicked": attachmentEvent.extend({
    source: oneOf(AttachmentActionEventSource).describe("The source of the action, e.g., toolbar or list"),
    totalFiles: number().describe("Total number of files"),
    count: number().describe("Number of files selected for download"),
  }),
  "attachments.action.delete.clicked": attachmentEvent.extend({
    count: number().describe("Number of files selected for deletion"),
    totalFiles: number().describe("Total number of files"),
  }),
  "attachments.action.rename.clicked": attachmentEvent,
  "attachments.action.open.clicked": attachmentEvent,
  "attachments.files.delete.cancelled": attachmentEvent.extend({
    count: number().optional().describe("Number of files deleted"),
    totalFiles: number().describe("Total number of files prior to deletion"),
  }),
  "attachments.files.deleted": attachmentEvent.extend({
    count: number().optional().describe("Number of files deleted"),
    totalFiles: number().describe("Total number of files prior to deletion"),
  }),
  "attachments.file.renamed": attachmentEvent.extend({ changed: boolean().describe("Whether the file name changed") }),
  "attachments.file.rename.cancelled": attachmentEvent,

  "attachments.bulk.modal.opened": attachmentEvent,
  "attachments.bulk.modal.closed": attachmentEvent,
};
